/**
 * FederatedFromBadge — V22.9.6
 *
 * Tiny inline badge that renders only when a row has a `source_profile_id`.
 * On click, opens a Popover with a snapshot of the originating Profile Gate
 * submission (asset_type, owner, criticality, RTO/RPO, submitted_by, ts).
 *
 * Drop-in usage:
 *   <FederatedFromBadge api={api} sourceProfileId={asset.source_profile_id} />
 *
 * Renders nothing when sourceProfileId is falsy — safe to sprinkle everywhere.
 */
import { useState } from "react";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Sparkles, Loader2, AlertCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function FederatedFromBadge({ api, sourceProfileId, testId, onRefederated }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [refederating, setRefederating] = useState(false);

  if (!sourceProfileId) return null;

  const handleOpen = async (next) => {
    setOpen(next);
    if (next && !profile && !loading) {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/asset-profiles/by-id/${sourceProfileId}`);
        setProfile(res.data || null);
      } catch (e) {
        setError(e.response?.data?.detail || "Failed to load source profile");
      } finally {
        setLoading(false);
      }
    }
  };

  const stop = (e) => e.stopPropagation();

  const handleRefederate = async (e) => {
    stop(e);
    setRefederating(true);
    try {
      const res = await api.post(`/asset-profiles/${sourceProfileId}/re-federate`);
      const stats = res.data?.federation || {};
      const total = Object.values(stats).reduce((s, n) => s + (n || 0), 0);
      toast.success(`Re-federated · ${total} row(s) refreshed across ${Object.keys(stats).length} collections`);
      onRefederated?.(stats);
    } catch (err) {
      const detail = err.response?.data?.detail || "Re-federation failed";
      toast.error(detail);
    } finally {
      setRefederating(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <button
          onClick={stop}
          className="inline-flex items-center"
          data-testid={testId || `federated-from-badge-${sourceProfileId}`}
          title="Federated from a Profile Gate submission — click for source"
        >
          <Badge
            variant="outline"
            className="text-[9px] px-1.5 py-0 h-4 border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 cursor-pointer gap-0.5"
          >
            <Sparkles className="w-2.5 h-2.5" />
            Federated
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0 overflow-hidden"
        onClick={stop}
        data-testid="federated-from-popover"
      >
        <div className="bg-gradient-to-br from-violet-50 to-sky-50 border-b border-violet-100 px-4 py-2.5">
          <div className="flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-violet-600" />
            <span className="text-[11px] font-semibold text-violet-700 uppercase tracking-wide">
              Source · Profile Gate
            </span>
          </div>
          <p className="text-[10px] text-slate-500 mt-0.5">
            This row was created by federation. Single source of truth.
          </p>
        </div>

        <div className="p-4 text-[12px] space-y-2">
          {loading && (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
            </div>
          )}
          {error && (
            <div className="flex items-start gap-1.5 text-rose-600">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span className="text-[11px]">{error}</span>
            </div>
          )}
          {profile && (
            <>
              <Row label="Asset Name" value={profile.asset_name} mono />
              <Row label="Asset Type" value={profile.asset_type} />
              <Row label="Owner" value={profile.owner_name || "—"} />
              <Row label="Owner Email" value={profile.owner_email || "—"} mono small />
              <Row label="Criticality" value={profile.business_criticality || "—"} />
              <Row label="Tier" value={profile.tier || "—"} />
              {profile.rto_hours != null && <Row label="RTO" value={`${profile.rto_hours} h`} />}
              {profile.rpo_hours != null && <Row label="RPO" value={`${profile.rpo_hours} h`} />}
              <Row label="Submitted By" value={profile.submitted_by_email || "—"} mono small />
              <Row
                label="Submitted At"
                value={profile.created_at ? new Date(profile.created_at).toLocaleString() : "—"}
                small
              />
              <div className="pt-1.5 mt-2 border-t border-slate-100">
                <span className="text-[9px] uppercase tracking-wide text-slate-400">
                  Profile ID
                </span>
                <p
                  className="text-[10px] font-mono text-slate-600 truncate"
                  data-testid="federated-from-profile-id"
                  title={profile.id}
                >
                  {profile.id}
                </p>
              </div>

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRefederate}
                disabled={refederating}
                className="w-full mt-3 h-7 text-[11px] border-violet-200 text-violet-700 hover:bg-violet-50 gap-1.5"
                data-testid="federated-from-refederate-btn"
              >
                {refederating ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <RefreshCw className="w-3 h-3" />
                )}
                Re-federate downstream rows
              </Button>
              <p className="text-[9.5px] text-slate-400 leading-snug mt-1.5">
                Replays the fan-out across all dependent collections. Idempotent &amp; audit-logged (event: <code className="text-[9px]">asset_profile.re_federated</code>).
              </p>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const Row = ({ label, value, mono, small }) => (
  <div className="flex justify-between gap-3">
    <span className="text-[10px] uppercase tracking-wide text-slate-400 flex-shrink-0">
      {label}
    </span>
    <span
      className={`text-right text-slate-700 ${mono ? "font-mono" : ""} ${small ? "text-[11px]" : ""} truncate`}
      title={typeof value === "string" ? value : ""}
    >
      {value}
    </span>
  </div>
);
